import {
  Add as AddIcon,
  Cable as PortIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Refresh as ResetIcon,
  Router as RouterIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AdminEmptyState,
  AdminFilterPanel,
  PageShell,
  AdminTableActions,
  AdminTableLoadingRow,
  AdminTablePanel,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useAuth } from "@/contexts/AuthContext";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerConfig, CustomerService } from "@/services/features/CustomerService";
import { RFIDReader, RFIDReaderService } from "@/services/features/RFIDReaderService";
import { Store, StoreService } from "@/services/features/StoreService";
import { formatStoreSelectorLabel } from "@/utils/store";

interface CustomerOption {
  id: number;
  label: string;
}

interface StoreOption {
  id: number;
  label: string;
  customerId: number;
}

type ReaderSortColumn = "name" | "customer" | "store" | "ports";

const rfidReaderService = RFIDReaderService.getInstance();
const storeService = StoreService.getInstance();
const customerService = CustomerService.getInstance();

const RFIDReaders: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const localizedTablePagination = useLocalizedTablePagination();

  const [allReaders, setAllReaders] = useState<RFIDReader[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [configs, setConfigs] = useState<CustomerConfig[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isResettingDeduplication, setIsResettingDeduplication] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const storeMap = useMemo(() => new Map(stores.map((store) => [store.id, store])), [stores]);
  const configMap = useMemo(
    () => new Map(configs.map((config) => [config.customerId, config])),
    [configs],
  );

  const customerOptions: CustomerOption[] = useMemo(
    () => customers.map((customer) => ({ id: customer.id, label: customer.name })),
    [customers],
  );
  const storeOptions: StoreOption[] = useMemo(
    () =>
      stores.map((store) => ({
        id: store.id,
        label: formatStoreSelectorLabel(store, t, user?.role, "name"),
        customerId: store.customerId,
      })),
    [stores, t, user?.role],
  );

  const filteredReaders = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return allReaders.filter((reader) => {
      const store = storeMap.get(reader.storeId);
      const matchesCustomer = !selectedCustomer || store?.customerId === selectedCustomer.id;
      const matchesStore = !selectedStore || reader.storeId === selectedStore.id;
      const matchesSearch = !normalizedQuery || reader.name.toLowerCase().includes(normalizedQuery);

      return matchesCustomer && matchesStore && matchesSearch;
    });
  }, [allReaders, searchQuery, selectedCustomer, selectedStore, storeMap]);

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    RFIDReader,
    ReaderSortColumn
  >(
    filteredReaders,
    (reader, column) => {
      const store = storeMap.get(reader.storeId);
      const config = store ? configMap.get(store.customerId) : undefined;

      return {
        name: reader.name,
        customer: config?.customerName,
        store: store?.name,
        ports: reader.ports.length,
      }[column];
    },
    "name",
  );

  const paginatedReaders = sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const statistics = useMemo(
    () => ({
      totalReaders: allReaders.length,
      totalPorts: allReaders.reduce((sum, reader) => sum + reader.ports.length, 0),
      activeStores: new Set(allReaders.map((reader) => reader.storeId)).size,
    }),
    [allReaders],
  );

  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    setAllReaders([]);

    try {
      const configResponse = await customerService.getAllCustomerConfigs();
      const customerResponse = await customerService.getAllCustomers();
      const storeResponse = await storeService.getStores();
      const readerResponse = await rfidReaderService.getAllReaders();

      if (configResponse.success && configResponse.data) setConfigs(configResponse.data);
      if (customerResponse.success && customerResponse.data) setCustomers(customerResponse.data);
      if (storeResponse.data) setStores(storeResponse.data);
      if (readerResponse.success && readerResponse.data) setAllReaders(readerResponse.data);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    document.title = t("management.devices.rfid.readers.documentTitle");
  }, [t]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCustomer, selectedStore]);

  const handleResetDeduplication = async (readerName: string) => {
    setIsResettingDeduplication(true);

    try {
      const response = await rfidReaderService.resetDeduplication(readerName);
      if (!response.success) throw new Error(response.error);

      enqueueSnackbar(
        t("management.devices.rfid.readers.notifications.deduplicationResetSuccessfully"),
        { variant: "success" },
      );
    } catch {
      enqueueSnackbar(t("management.devices.rfid.readers.notifications.failedResetDeduplication"), {
        variant: "error",
      });
    } finally {
      setIsResettingDeduplication(false);
    }
  };

  const handleCustomerChange = (nextCustomer: CustomerOption | null) => {
    setSelectedCustomer(nextCustomer);
    if (nextCustomer && selectedStore?.customerId !== nextCustomer.id) setSelectedStore(null);
  };

  const handleClearFilters = () => {
    setSelectedCustomer(null);
    setSelectedStore(null);
  };

  const handleTableSort = (column: ReaderSortColumn) => {
    handleSort(column);
    setPage(0);
  };

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={sortedItems.length}
      page={page}
      onPageChange={(_event, nextPage) => setPage(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      rowsPerPageOptions={isMobile ? [] : [25, 50, 100, 250]}
      {...localizedTablePagination}
    />
  );

  return (
    <PageShell
      breadcrumbs="admin"
      headerActions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => void navigate("/admin/hardware/rfid-readers/add")}
        >
          {t("management.devices.rfid.readers.actions.addNewReader")}
        </Button>
      }
      icon={<RouterIcon />}
      title={t("management.devices.rfid.readers.title")}
      titleAccessory={<LoadingRing loading={isLoading || isResettingDeduplication} />}
    >
      <>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <RouterIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("management.devices.rfid.readers.text.totalReaders")}
                  </Typography>
                  <Typography variant="h4">{statistics.totalReaders}</Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <PortIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("management.devices.rfid.readers.text.totalPorts")}
                  </Typography>
                  <Typography variant="h4">{statistics.totalPorts}</Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <StoreIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("management.devices.rfid.readers.text.activeStores")}
                  </Typography>
                  <Typography variant="h4">{statistics.activeStores}</Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        <AdminFilterPanel sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                sx={{ width: "100%" }}
                options={customerOptions}
                value={selectedCustomer ? [selectedCustomer] : []}
                onChange={(next) => handleCustomerChange(next[0] ?? null)}
                label={t("management.devices.rfid.readers.fields.selectCustomer")}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.id}
                loading={isLoading}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                sx={{ width: "100%" }}
                options={storeOptions.filter((store) =>
                  selectedCustomer ? store.customerId === selectedCustomer.id : true,
                )}
                value={selectedStore ? [selectedStore] : []}
                onChange={(next) => setSelectedStore(next[0] ?? null)}
                label={t("management.devices.devices.fields.store")}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.id}
                loading={isLoading}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} md="auto" sx={{ alignSelf: "center" }}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                color="primary"
                size="medium"
                disabled={!selectedCustomer && !selectedStore}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                {t("common.clearFilters")}
              </Button>
            </Grid>
          </Grid>
        </AdminFilterPanel>

        <AdminTablePanel
          header={
            <Box
              sx={{
                alignItems: { lg: "center", xs: "stretch" },
                display: "flex",
                flexDirection: { lg: "row", xs: "column" },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                placeholder={t("management.devices.rfid.readers.placeholders.searchByReaderName")}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                inputProps={{
                  "aria-label": t(
                    "management.devices.rfid.readers.placeholders.searchByReaderName",
                  ),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Box sx={{ flexShrink: 0, "& .MuiTablePagination-root": { p: 0 } }}>
                {renderPagination()}
              </Box>
            </Box>
          }
          footer={
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{renderPagination()}</Box>
          }
          containerProps={{ sx: { overflowX: "auto" } }}
          tableProps={{ size: isMobile ? "small" : "medium" }}
          tableSx={{
            minWidth: 650,
            "& .MuiTableHead-root .MuiTableCell-root": {
              borderBottom: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
              borderTop: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
            },
            "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
              borderInlineStart: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
              borderStartStartRadius: 0,
            },
            "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
              borderInlineEnd: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
              borderStartEndRadius: 0,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <SortableTableCell
                activeColumn={sortColumn}
                column="name"
                direction={sortDirection}
                onSort={handleTableSort}
              >
                {t("management.devices.devices.fields.name")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="customer"
                direction={sortDirection}
                onSort={handleTableSort}
              >
                {t("management.devices.rfid.readers.fields.customer")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="store"
                direction={sortDirection}
                onSort={handleTableSort}
              >
                {t("management.devices.devices.fields.store")}
              </SortableTableCell>
              <SortableTableCell
                align="center"
                activeColumn={sortColumn}
                column="ports"
                direction={sortDirection}
                onSort={handleTableSort}
              >
                {t("management.devices.rfid.readers.text.mappedPorts")}
              </SortableTableCell>
              <TableCell align="right">{t("management.devices.devices.fields.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <AdminTableLoadingRow colSpan={5} label={t("common.loading")} />
            ) : paginatedReaders.length > 0 ? (
              paginatedReaders.map((reader) => {
                const store = storeMap.get(reader.storeId);
                const config = store ? configMap.get(store.customerId) : undefined;
                const mappedPortNumbers = new Set(
                  reader.ports
                    .filter((port): port is { port: number } => typeof port?.port === "number")
                    .map((port) => port.port),
                );

                return (
                  <TableRow key={reader.name} hover>
                    <TableCell>
                      <Box
                        component="code"
                        sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                      >
                        {reader.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {config?.customerName || t("management.devices.devices.empty.notAvailable")}
                    </TableCell>
                    <TableCell>
                      {store?.name || t("management.devices.devices.empty.notAvailable")}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        aria-label={t("management.devices.rfid.readers.text.mappedPorts")}
                        sx={{
                          display: "inline-grid",
                          gap: 0.5,
                          gridTemplateColumns: "repeat(4, 6px)",
                        }}
                      >
                        {Array.from({ length: 8 }, (_, index) => {
                          const isMapped = mappedPortNumbers.has(index + 1);

                          return (
                            <Box
                              key={index}
                              aria-hidden="true"
                              sx={{
                                backgroundColor: isMapped ? "success.main" : "action.disabled",
                                borderRadius: "50%",
                                height: 6,
                                width: 6,
                              }}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <AdminTableActions
                        actions={[
                          {
                            icon: <ResetIcon />,
                            hint: t("management.devices.rfid.readers.tooltips.resetDeduplication"),
                            action: () => void handleResetDeduplication(reader.name),
                          },
                          {
                            icon: <ViewIcon />,
                            hint: t("management.devices.rfid.readers.tooltips.viewDetails"),
                            action: () =>
                              void navigate(`/admin/hardware/rfid-readers/${reader.name}`),
                          },
                          {
                            icon: <EditIcon />,
                            hint: t("management.devices.rfid.readers.tooltips.editReader"),
                            action: () =>
                              void navigate(`/admin/hardware/rfid-readers/edit/${reader.name}`),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="table-empty-state">
                <TableCell colSpan={5}>
                  <AdminEmptyState
                    icon={<SearchIcon sx={{ fontSize: 40 }} />}
                    text={
                      searchQuery || selectedCustomer || selectedStore
                        ? t("management.devices.rfid.readers.empty.noMatchingReaders")
                        : t("management.devices.rfid.readers.empty.noReaders")
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </AdminTablePanel>
      </>
    </PageShell>
  );
};

export default RFIDReaders;
